import { useEffect, useState } from "react";
import { bookingById } from "services/bookingApi";

interface UseBookingByIdReturn {
  data: any;
  isFetching: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Custom hook to fetch booking details by booking ID
 * Implements lazy loading - only fetches when shouldFetch is true
 * Useful for lazy-loading booking data on tab click
 *
 * @param bookingId - The booking ID to fetch
 * @param shouldFetch - Whether to fetch data (boolean)
 * @returns Object containing data, isFetching status, error, and refetch function
 */
export const useBookingById = (
  bookingId: string | undefined,
  shouldFetch: boolean = true
): UseBookingByIdReturn => {
  const [data, setData] = useState<any>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBooking = async () => {
    if (!bookingId || !shouldFetch) {
      return;
    }

    setIsFetching(true);
    setError(null);
    try {
      const response = await bookingById(bookingId);
      setData(response);
    } catch (err) {
      console.error("Error fetching booking:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch booking details");
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (shouldFetch && bookingId) {
      fetchBooking();
    }
  }, [bookingId, shouldFetch]);

  const refetch = () => {
    fetchBooking();
  };

  return {
    data,
    isFetching,
    error,
    refetch,
  };
};
